import React, { useEffect, useState } from "react";
import DataTableBase from "../../../../Components/Common/DataTableBase/DataTableBase";
import { t } from "i18next";
import { useLinksStore } from "../../../../stores/Assets/links";
import { useConfirmDialogStore } from "../../../../stores/Modal/ConfirmDialogStore";
import { useSiteStore } from "../../../../stores/Assets/site";
import { canEdit } from "../../../../helpers/permissions";
import { Button, Input } from "reactstrap";
import ReactSelect from "react-select";
import { reactSelectStyling } from "../../../../Components/Common/Forms/FormikInputHelper";
import Skeleton from "../../../../Components/Common/Skeleton";

const SiteLinksTable = ({ onAddClick }) => {
  const { getSites, sites } = useSiteStore((state) => state);
  const {
    getSiteLinks,
    siteLinks,
    removeSiteLink,
    siteLinksPagination,
  } = useLinksStore((state) => state);
  
  const { showConfirm } = useConfirmDialogStore((state) => state);
  
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [formattedSiteLinks, setFormattedSiteLinks] = useState([]);
  const [canEditLink, setCanEditLink] = useState(false);
  const rowsPerPage = 10;

  // Filter states
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedSiteOption, setSelectedSiteOption] = useState(null);
  const [emailSearch, setEmailSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await getSiteLinks({ page: 1, perPage: rowsPerPage });
      await getSites();
      setIsLoading(false);
    };
    fetchData();
  }, [getSiteLinks, getSites]);

  useEffect(() => {
    const checkPermission = async () => {
      const res = await canEdit("link");
      setCanEditLink(res);
    };
    checkPermission();
  }, []);

  // Format site links
  useEffect(() => {
    if (siteLinks) {
      setFormattedSiteLinks(siteLinks);
    }
  }, [siteLinks]);

  useEffect(() => {
    setTotalRows(siteLinksPagination?.totalItems || 0);
  }, [siteLinksPagination]);

  // Convert sites to React Select options
  const siteFilterOptions = [
    { value: "", label: "All Sites" },
    ...sites.map((site) => ({
      value: site.site_id,
      label: `${site.site_id} - ${site.name}`,
    })),
  ];

  // Columns for Site Links table
  const siteColumns = [
    {
      name: t("Site ID"),
      selector: (row) => row?.site_id,
      sortable: true,
      wrap: true,
      width: "120px",
    },
    {
      name: t("Site Name"),
      selector: (row) => row?.site_name,
      sortable: true,
      wrap: true,
    },
    {
      name: t("User Email"),
      selector: (row) => row?.user_email,
      sortable: true,
      wrap: true,
    },
  ];

  const deleteSiteLink = async (body) => {
    try {
      setIsLoading(true);
      await removeSiteLink(body);
      setIsLoading(false);
    } catch (e) {
      console.log(e);
      setIsLoading(false);
    }
  };

  const onChangePage = (page) => {
    setIsLoading(true);
    getSiteLinks({ page, perPage: rowsPerPage }).finally(() => setIsLoading(false));
  };

  // Auto-apply filters when they change
  useEffect(() => {
    let filtered = siteLinks;

    if (selectedSite) {
      filtered = filtered.filter((link) => link.site_id === parseInt(selectedSite));
    }

    if (emailSearch && emailSearch.trim() !== "") {
      filtered = filtered.filter((link) =>
        link.user_email?.toLowerCase().includes(emailSearch.trim().toLowerCase())
      );
    }

    setFormattedSiteLinks(filtered);
  }, [selectedSite, emailSearch, siteLinks]);

  return (
    <div className="mt-4">
      <DataTableBase
        tableTitle={"SITE LINKS (Direct Site Access)"}
        customHeader={
          sites.length === 0 ? (
            // Skeleton loading state
            <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
              <div className="w-100" style={{ flex: '0 0 10%' }}>
                <Skeleton variant="button" />
              </div>
              <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2" style={{ flex: '0 0 60%' }}>
                <div className="d-flex align-items-center gap-2 w-100">
                  <h6 className="text-nowrap mb-0">Site: </h6>
                  <div className="flex-grow-1">
                    <Skeleton variant="input" />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 w-100">
                  <h6 className="text-nowrap mb-0">Email: </h6>
                  <div className="flex-grow-1">
                    <Skeleton variant="input" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column flex-md-row align-items-stretch justify-content-between gap-2 w-100 mb-2">
              {canEditLink ? (
                <Button
                  color="success"
                  onClick={onAddClick}
                  className="d-flex align-items-center justify-content-center w-100"
                  style={{ flex: '0 0 10%', padding: '0.4rem 2rem', whiteSpace: 'nowrap', fontSize:"13px"}}
                >
                  + {t("Add Site Link")}
                </Button>
              ) : null}
              <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100 px-2" style={{ flex: '0 0 60%' }}>
                {sites.length > 0 && (
                  <div className="d-flex align-items-center gap-2 w-100">
                    <h6 className="text-nowrap mb-0">Site: </h6>
                    <div className="flex-grow-1">
                      <ReactSelect
                        {...reactSelectStyling}
                        options={siteFilterOptions}
                        value={selectedSiteOption}
                        onChange={(option) => {
                          setSelectedSiteOption(option);
                          setSelectedSite(option?.value || "");
                        }}
                        placeholder={t("Select Site...")}
                        isClearable
                      />
                    </div>
                  </div>
                )}

                <div className="d-flex align-items-center gap-2 mr-3 w-100">
                  <h6 className="text-nowrap mb-0">Email: </h6>
                  <Input
                    className="flex-grow-1"
                    id="site-email-search"
                    type="text"
                    placeholder="Search by email..."
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )
        }
        data={formattedSiteLinks}
        columns={siteColumns}
        loading={isLoading}
        paginationTotalRows={totalRows}
        onChangePage={onChangePage}
        onHeaderAddBtnClick={null}
        showSearch={false}
        showActionColumn={canEditLink}
        showSubHeader={false}
        showActionButtons={false}
        customActionBtns={(row) => (
          <>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                showConfirm(
                  () => {
                    deleteSiteLink({
                      email: row?.user_email,
                      site_id: row?.site_id,
                    });
                  },
                  () => {
                    console.log("Cancelled");
                  },
                  "Confirmation",
                  `Are you sure you want to remove this user from site access?`
                );
              }}
              title="Delete"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      />
    </div>
  );
};

export default SiteLinksTable;

